import { useEffect, useState } from "react";
import { ShieldCheck, UserCheck, UserX, Search, MessageSquare, Send, PauseCircle, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ReactivationDialog } from "@/components/contacts/ReactivationDialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { StopWordsCard } from "@/components/StopWordsCard";
import { ConsentShadowPanel } from "@/components/ConsentShadowPanel";

export default function Consent() {
  const [contacts, setContacts] = useState([]);
  const [reactivateId, setReactivateId] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "all");
  const [inbound, setInbound] = useState([]);
  const [simPhone, setSimPhone] = useState("");
  const [simMsg, setSimMsg] = useState("STOP");
  const [exporting, setExporting] = useState(false);

  const exportUnsubscribed = async () => {
    setExporting(true);
    try {
      const res = await api.get("/contacts/export-unsubscribed", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `disiscritti-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV disiscritti scaricato");
    } catch { toast.error("Errore durante l'esportazione"); }
    finally { setExporting(false); }
  };

  const load = async () => {
    const [c, inb] = await Promise.all([
      api.get("/contacts", { params: search ? { search } : {} }),
      api.get("/whatsapp/inbound"),
    ]);
    setContacts(c.data);
    setInbound(inb.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const toggle = async (id, status) => {
    if (status !== "subscribed") { setReactivateId(id); return; }  // H0
    try {
      await api.post(`/contacts/${id}/toggle-consent`);
      toast.success("Consenso aggiornato");
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Operazione non consentita"); }
    load();
  };

  const sendStop = async () => {
    if (!simPhone) { toast.error("Inserisci un numero di telefono"); return; }
    const { data } = await api.post("/whatsapp/inbound", { phone: simPhone, message: simMsg || "STOP" });
    if (data.action === "unsubscribed")
      toast.success(`${data.contact_name || simPhone} disiscritto automaticamente`);
    else if (data.action === "subscribed")
      toast.success(`${data.contact_name || simPhone} re-iscritto`);
    else
      toast.info(data.matched ? "Messaggio ricevuto, nessuna azione" : "Numero non presente in rubrica");
    setSimMsg("STOP");
    load();
  };

  const subscribed = contacts.filter((c) => c.status === "subscribed");
  const unsubscribed = contacts.filter((c) => c.status === "unsubscribed");
  const paused = contacts.filter((c) => c.status === "paused");
  const shown = tab === "subscribed" ? subscribed : tab === "unsubscribed" ? unsubscribed : tab === "paused" ? paused : contacts;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-100">Centro Consensi (GDPR)</h1>
          <p className="text-slate-400 text-sm mt-1">Gestisci iscrizioni e disiscrizioni dei clienti</p>
        </div>
        <Button variant="outline" onClick={exportUnsubscribed} disabled={exporting} data-testid="consent-export-unsubscribed-button"
                className="bg-[#1E293B] border-[#1F2937] text-slate-200 hover:text-[#10B981]">
          {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Esporta disiscritti (CSV)
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { id: "all", label: "Totali", value: contacts.length, color: "#3B82F6" },
          { id: "subscribed", label: "Iscritti", value: subscribed.length, color: "#10B981" },
          { id: "paused", label: "In pausa", value: paused.length, color: "#F59E0B" },
          { id: "unsubscribed", label: "Disiscritti", value: unsubscribed.length, color: "#EF4444" },
        ].map((s) => (
          <button key={s.id} onClick={() => setTab(s.id)} data-testid={`consent-tab-${s.id}`}
                  className={`bg-[#111827]/80 border rounded-2xl p-5 text-left transition-all ${
                    tab === s.id ? "border-[#10B981]" : "border-[#1F2937] hover:border-[#10B981]/40"
                  }`}>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{s.label}</p>
            <p className="font-display text-3xl font-extrabold mt-2" style={{ color: s.color }}>
              {s.value.toLocaleString("it-IT")}
            </p>
          </button>
        ))}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input data-testid="consent-search-input" placeholder="Cerca contatto..." value={search}
               onChange={(e) => setSearch(e.target.value)}
               className="pl-10 bg-[#111827] border-[#1F2937] text-slate-100" />
      </div>

      <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl divide-y divide-[#1F2937]/60" data-testid="consent-list">
        {shown.map((c) => (
          <div key={c.id} className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-3">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
                c.status === "subscribed" ? "bg-[#10B981]/15" : c.status === "paused" ? "bg-[#F59E0B]/15" : "bg-[#EF4444]/15"
              }`}>
                {c.status === "subscribed"
                  ? <UserCheck className="h-5 w-5 text-[#10B981]" />
                  : c.status === "paused" ? <PauseCircle className="h-5 w-5 text-[#F59E0B]" /> : <UserX className="h-5 w-5 text-[#EF4444]" />}
              </div>
              <div>
                <p className="text-sm text-slate-200">{c.name}</p>
                <p className="text-xs text-slate-500 font-mono2">{c.phone}{c.status === "unsubscribed" && c.unsub_reason ? ` · motivo: ${c.unsub_reason}` : ""}{c.status === "paused" && c.paused_until ? ` · fino al ${new Date(c.paused_until).toLocaleDateString("it-IT")}` : ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={`border-0 text-xs ${
                c.status === "subscribed" ? "bg-[#10B981]/15 text-[#10B981]" : c.status === "paused" ? "bg-[#F59E0B]/15 text-[#F59E0B]" : "bg-[#EF4444]/15 text-[#EF4444]"
              }`}>
                {c.status === "subscribed" ? "Iscritto" : c.status === "paused" ? "In pausa" : "Disiscritto"}
              </Badge>
              <Switch checked={c.status === "subscribed"} data-testid={`consent-switch-${c.id}`}
                      onCheckedChange={() => toggle(c.id, c.status)} />
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="text-slate-500 text-center py-10">Nessun contatto.</p>}
      </div>

      {/* STOP simulator */}
      <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5" data-testid="stop-simulator">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="h-5 w-5 text-[#10B981]" />
          <h3 className="font-display text-lg font-semibold text-slate-200">Simula messaggio in arrivo</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Modalità demo: invia <span className="font-mono2 text-[#EF4444]">STOP</span> per disiscrivere automaticamente un contatto,
          oppure <span className="font-mono2 text-[#10B981]">START</span> per re-iscriverlo. Copia un numero dalla lista sotto.
        </p>
        <div className="flex flex-wrap gap-2">
          <Input data-testid="stop-phone-input" value={simPhone} onChange={(e) => setSimPhone(e.target.value)}
                 placeholder="+39..." className="flex-1 min-w-[160px] bg-[#0A0E17] border-[#1F2937] text-slate-100 font-mono2" />
          <Input data-testid="stop-message-input" value={simMsg} onChange={(e) => setSimMsg(e.target.value)}
                 placeholder="STOP" className="w-28 bg-[#0A0E17] border-[#1F2937] text-slate-100" />
          <Button onClick={sendStop} data-testid="stop-send-button"
                  className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            <Send className="h-4 w-4 mr-2" /> Invia
          </Button>
        </div>
        {inbound.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="inbound-log">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Messaggi ricevuti</p>
            {inbound.slice(0, 6).map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 text-sm bg-[#0A0E17] rounded-lg px-3 py-2">
                <span className="font-mono2 text-slate-300 shrink-0">{m.phone}</span>
                <span className="text-slate-400 truncate flex-1">"{m.message}"</span>
                <Badge className={`border-0 text-xs shrink-0 ${
                  m.action === "unsubscribed" ? "bg-[#EF4444]/15 text-[#EF4444]"
                  : m.action === "subscribed" ? "bg-[#10B981]/15 text-[#10B981]"
                  : "bg-slate-500/15 text-slate-400"
                }`}>
                  {m.action === "unsubscribed" ? "Disiscritto" : m.action === "subscribed" ? "Iscritto" : "Nessuna azione"}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      <StopWordsCard />

      <ConsentShadowPanel />

      <div className="flex items-start gap-3 bg-[#10B981]/5 border border-[#10B981]/20 rounded-2xl p-5">
        <ShieldCheck className="h-6 w-6 text-[#10B981] shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-slate-200 font-medium">Conformità GDPR</p>
          <p className="text-xs text-slate-400 mt-1">
            I contatti disiscritti vengono automaticamente esclusi da tutte le campagne massive e segnalati con il badge «Disiscritto» in Chat.
            Un cliente può disiscriversi in qualsiasi momento rispondendo "STOP" (o una delle parole configurate sopra) e re-iscriversi scrivendo "Sì" o "Iscrivimi".
          </p>
        </div>
      </div>
      <ReactivationDialog contactId={reactivateId} onClose={() => setReactivateId(null)} onDone={load} />
    </div>
  );
}
